import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';

const TOKENS = {
  light: {
    surface: '#ffffff', surfaceAlt: '#fafaf8', sunken: '#efefec',
    border: '#e7e7e3', text: '#1a1a1f', textSecondary: '#5a5a66', textMuted: '#8a8a96',
    danger: '#dc2626', dangerSoft: '#fee2e2', dangerText: '#991b1b',
    warning: '#d97706', warningSoft: '#fef3c7', warningText: '#92400e',
    success: '#0d9488', successSoft: '#ccfbf1', successText: '#115e59',
    accent: '#4f46e5', accentSoft: '#eef2ff', accentText: '#3730a3',
    shadow: '0 4px 16px rgba(15,15,25,0.10), 0 1px 3px rgba(15,15,25,0.06)',
    shadowModal: '0 8px 32px rgba(15,15,25,0.14), 0 2px 8px rgba(15,15,25,0.08)',
  },
  dark: {
    surface: '#1a1c23', surfaceAlt: '#22242c', sunken: '#0f1015',
    border: '#2a2c36', text: '#f0f0f5', textSecondary: '#a8a8b8', textMuted: '#6b6b7a',
    danger: '#f87171', dangerSoft: 'rgba(248,113,113,0.12)', dangerText: '#fca5a5',
    warning: '#fbbf24', warningSoft: 'rgba(251,191,36,0.12)', warningText: '#fde68a',
    success: '#2dd4bf', successSoft: 'rgba(45,212,191,0.12)', successText: '#5eead4',
    accent: '#818cf8', accentSoft: 'rgba(129,140,248,0.12)', accentText: '#c7d2fe',
    shadow: '0 4px 16px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.25)',
    shadowModal: '0 8px 32px rgba(0,0,0,0.50), 0 2px 8px rgba(0,0,0,0.30)',
  },
  black: {
    surface: '#101015', surfaceAlt: '#15151c', sunken: '#06060a',
    border: '#1f1f28', text: '#f5f5fa', textSecondary: '#9a9aab', textMuted: '#5a5a6a',
    danger: '#f87171', dangerSoft: 'rgba(248,113,113,0.10)', dangerText: '#fca5a5',
    warning: '#fbbf24', warningSoft: 'rgba(251,191,36,0.10)', warningText: '#fde68a',
    success: '#2dd4bf', successSoft: 'rgba(45,212,191,0.10)', successText: '#5eead4',
    accent: '#818cf8', accentSoft: 'rgba(129,140,248,0.10)', accentText: '#c7d2fe',
    shadow: '0 4px 16px rgba(0,0,0,0.55), 0 1px 3px rgba(0,0,0,0.40)',
    shadowModal: '0 8px 32px rgba(0,0,0,0.70), 0 2px 8px rgba(0,0,0,0.50)',
  },
};

const TYPE_CONFIG = {
  error:   { icon: AlertCircle,   colorKey: 'danger'  },
  warning: { icon: AlertTriangle, colorKey: 'warning' },
  success: { icon: CheckCircle,   colorKey: 'success' },
  info:    { icon: Info,          colorKey: 'accent'  },
};

const ToastContext = createContext(null);

let _nextId = 0;

// ─── Toast item ────────────────────────────────────────────────────────────────

function ToastItem({ toast, onRemove, tk }) {
  const [exiting, setExiting] = React.useState(false);

  const dismiss = React.useCallback(() => {
    setExiting(true);
    setTimeout(() => onRemove(toast.id), 220);
  }, [toast.id, onRemove]);

  React.useEffect(() => {
    const t = setTimeout(dismiss, 4500);
    return () => clearTimeout(t);
  }, [dismiss]);

  const { icon: Icon, colorKey } = TYPE_CONFIG[toast.type] || TYPE_CONFIG.error;
  const accentColor = tk[colorKey];

  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '11px 12px 11px 14px',
        background: tk.surface,
        border: `1px solid ${tk.border}`,
        borderInlineStart: `3px solid ${accentColor}`,
        borderRadius: 10,
        boxShadow: tk.shadow,
        width: 320, maxWidth: 'calc(100vw - 32px)',
        fontFamily: '"DM Sans", "Cairo", sans-serif',
        animation: exiting
          ? 'counter-toast-out 0.22s cubic-bezier(0.4,0,1,1) forwards'
          : 'counter-toast-in 0.22s cubic-bezier(0,0,0.2,1) forwards',
      }}
    >
      <Icon size={15} color={accentColor} style={{ flexShrink: 0, marginTop: 1 }} />
      <span style={{ flex: 1, fontSize: 12.5, lineHeight: 1.5, color: tk.text, wordBreak: 'break-word' }}>
        {toast.message}
      </span>
      <button
        onClick={dismiss}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: tk.textMuted, padding: '0 0 0 4px',
          display: 'flex', alignItems: 'center', flexShrink: 0,
          marginTop: 1,
        }}
      >
        <X size={13} />
      </button>
    </div>
  );
}

// ─── Confirm modal ─────────────────────────────────────────────────────────────

function ConfirmModal({ state, tk }) {
  const { message, detail, confirmLabel, cancelLabel, type, resolve } = state || {};

  React.useEffect(() => {
    if (!state) return;
    const handler = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); resolve(true); }
      if (e.key === 'Escape') { e.preventDefault(); resolve(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state, resolve]);

  if (!state) return null;

  const confirmColor =
    type === 'danger' ? tk.danger
      : type === 'warning' ? tk.warning
        : type === 'success' ? tk.success
          : tk.accent;

  const handleCancel = () => resolve(false);
  const handleConfirm = () => resolve(true);

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.52)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 99998,
        animation: 'counter-confirm-in 0.15s ease forwards',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
    >
      <div
        style={{
          background: tk.surface,
          border: `1px solid ${tk.border}`,
          borderRadius: 14,
          padding: '22px 24px',
          maxWidth: detail ? 460 : 400, width: '100%',
          margin: '0 16px',
          boxShadow: tk.shadowModal,
          fontFamily: '"DM Sans", "Cairo", sans-serif',
          animation: 'counter-confirm-slide 0.18s cubic-bezier(0,0,0.2,1) forwards',
        }}
      >
        <p style={{
          margin: detail ? '0 0 10px' : '0 0 20px',
          fontSize: 13.5, lineHeight: 1.55,
          color: tk.text, fontWeight: 500,
        }}>
          {message}
        </p>
        {detail ? (
          <div
            style={{
              margin: '0 0 18px',
              padding: '10px 12px',
              borderRadius: 10,
              background: tk.surfaceAlt,
              border: `1px solid ${tk.border}`,
              maxHeight: 220,
              overflowY: 'auto',
              fontSize: 12.5,
              lineHeight: 1.55,
              color: tk.textSecondary,
              whiteSpace: 'pre-line',
            }}
          >
            {detail}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '7px 16px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${tk.border}`, background: 'transparent',
              color: tk.textSecondary, fontSize: 13, fontFamily: 'inherit', fontWeight: 500,
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: '7px 16px', borderRadius: 8, cursor: 'pointer',
              border: 'none', background: confirmColor,
              color: '#fff', fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Hooks ─────────────────────────────────────────────────────────────────────

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx.showToast;
};

export const useConfirm = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useConfirm must be used within ToastProvider');
  return ctx.showConfirm;
};

// ─── Provider ──────────────────────────────────────────────────────────────────

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const { mode } = useSelector((state) => state.theme);
  const tk = TOKENS[mode] || TOKENS.dark;

  const showToast = useCallback((message, type = 'error') => {
    const id = ++_nextId;
    setToasts((prev) => {
      const next = [...prev, { id, message: String(message), type }];
      return next.length > 6 ? next.slice(next.length - 6) : next;
    });
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showConfirm = useCallback((message, {
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    type = 'danger',
    detail = null,
  } = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        message,
        detail,
        confirmLabel,
        cancelLabel,
        type,
        resolve: (result) => {
          setConfirmState(null);
          resolve(result);
        },
      });
    });
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showConfirm }}>
      {children}
      <style>{`
        @keyframes counter-toast-in {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0);    }
        }
        @keyframes counter-toast-out {
          from { opacity: 1; transform: translateX(0);    }
          to   { opacity: 0; transform: translateX(20px); }
        }
        @keyframes counter-confirm-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes counter-confirm-slide {
          from { opacity: 0; transform: scale(0.96) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0);      }
        }
        [dir="rtl"] .counter-toast-container {
          right: unset; left: 16px;
        }
      `}</style>

      {/* Confirm dialog */}
      <ConfirmModal state={confirmState} tk={tk} />

      {/* Toast stack */}
      <div
        className="counter-toast-container"
        style={{
          position: 'fixed', top: 16, right: 16,
          display: 'flex', flexDirection: 'column', gap: 8,
          zIndex: 99999, pointerEvents: 'none',
          alignItems: 'flex-end',
        }}
      >
        {toasts.map((toast) => (
          <div key={toast.id} style={{ pointerEvents: 'auto' }}>
            <ToastItem toast={toast} onRemove={removeToast} tk={tk} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
